// ============================================================
// PERRY — THE VILLAGE
// The first village. Rest, refresh, learn where the road goes,
// and meet Whyllie, who will sell you anything including things
// priced in your own blood.
//
// This screen is DOM over a painted gradient, not a 3D room — a
// walkable Hollowmere is a whole session of its own and is in the
// Roadmap. What matters now is that the loop CLOSES: fire → road
// → village → stronger → road.
// ============================================================

import { useState } from "react";
import { GM } from "./theme";
import { EmberDust, MetalRule, ensureKeyframes } from "./PerryUI";
import Outfitter from "./Outfitter";
import {
  villageFor, SPECIES, RULES, GLYPH,
  deriveAnimaCap, deriveMaxChao, deriveLyt, availablePoints, deriveStanding,
} from "./perryData";

const KF = `
@keyframes vilIn    { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
@keyframes vilLamp  { 0%,100% { opacity: 0.75; transform: scale(1); } 50% { opacity: 1; transform: scale(1.05); } }
@keyframes vilGrant { 0% { opacity: 0; transform: scale(0.9); } 60% { opacity: 1; transform: scale(1.04); } 100% { opacity: 1; transform: scale(1); } }
`;

export default function VillageScene({
  soul, crests, kindling, arrivedNow, runSummary,
  onBuy, onCustomize, onCodex, onMenu, onReplay, onDepart,
}) {
  ensureKeyframes("perry-village-keyframes", KF);
  const [tab, setTab] = useState(arrivedNow ? "arrive" : "hub");
  const [said, setSaid] = useState(0);
  const sp = SPECIES[soul?.species];
  const points = availablePoints(soul);
  // ══════════════════════════════════════════════════════════
  //  WHICH VILLAGE — R-07, closed 2026-07-29
  // ══════════════════════════════════════════════════════════
  // This file imported the `VILLAGE` alias, which is permanently
  // Hollowmere. Candlemarch has existed in the data since the
  // Drowned Mile shipped and this screen could never show it. One
  // function call — and `villageFor` is DERIVED off the milestone,
  // so nothing new is stored and nothing can drift.
  const V = villageFor(soul);
  // `said` indexes into V.speak, and the two villages have
  // different numbers of speakers — so it is taken modulo the
  // list every time it is read rather than trusted to be in range.
  const speaker = V.speak[said % V.speak.length];
  // A road east exists only if this village has one AND App gave
  // us a way to walk it. Candlemarch's is null until Act III.
  const canDepart = !!(V.depart && onDepart);

  const panel = {
    padding: "18px 22px", borderRadius: 12,
    background: "linear-gradient(175deg, #1c1009, #0d0705)",
    border: `1px solid ${GM.pageBorder}`,
    boxShadow: "0 12px 30px rgba(0,0,0,0.6)",
  };

  return (
    <div style={{
      position: "absolute", inset: 0, overflow: "auto", fontFamily: GM.fontUI,
      background: "linear-gradient(180deg, #0d0a14 0%, #1a1008 42%, #2a1a0c 72%, #0a0605 100%)",
    }}>
      {/* lantern haze */}
      <div style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 50% 78%, rgba(255,176,88,0.16) 0%, transparent 62%)",
        animation: "vilLamp 5s ease-in-out infinite",
      }} />
      <EmberDust count={22} tint="#ffc27a" />

      <div style={{
        position: "relative", minHeight: "100%", display: "flex",
        flexDirection: "column", alignItems: "center", padding: "36px 20px 48px",
      }}>
        {/* ── header ── */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{
            fontFamily: GM.fontD, fontSize: "clamp(30px, 7vw, 46px)", letterSpacing: 12,
            color: GM.vellum, textShadow: `0 0 40px ${GM.inkGold}, 0 3px 10px #000`,
          }}>{V.name}</div>
          <MetalRule width="min(300px, 70vw)" style={{ margin: "8px auto 8px" }} />
          <div style={{ fontSize: 10, letterSpacing: 5, color: GM.vellumFaint }}>
            {V.sub.toUpperCase()}
          </div>
        </div>

        {/* ── the traveller's line ── */}
        <div style={{
          display: "flex", gap: 18, flexWrap: "wrap", justifyContent: "center",
          marginBottom: 20, fontSize: 12, color: GM.vellumDim, letterSpacing: 1,
        }}>
          <span>{sp?.name || "Beb"}</span>
          <span style={{ color: GM.rose }}>❤ {deriveAnimaCap(soul)} ANIMA</span>
          <span style={{ color: GM.amethystBright }}>✦ {deriveMaxChao(soul)} CHAO</span>
          <span style={{ color: GM.inkGoldBright }}>⚔ {deriveLyt(soul)} LYT</span>
          <span style={{ color: GM.inkGoldBright }}>{GLYPH.kd} {kindling ?? 0}</span>
          <span style={{ color: GM.vellumFaint }}>{GLYPH.dc} {crests ?? 0}</span>
          {/* Renown was retired 2026-07-29 — standing is derived
              from ascensions bought, and the purse is printed above. */}
          <span>{deriveStanding(soul)}</span>
        </div>

        {/* ── ARRIVAL ── */}
        {tab === "arrive" ? (
          <div style={{ ...panel, width: "min(660px, 94vw)", animation: "vilIn 0.7s ease-out" }}>
            {V.arriveLines.map((l, i) => (
              <div key={i} style={{
                fontFamily: GM.fontBody, fontSize: 18, lineHeight: 1.65,
                color: GM.vellum, marginBottom: 12,
              }}>{l}</div>
            ))}
            <div style={{
              marginTop: 16, padding: "14px 16px", borderRadius: 10,
              background: "rgba(43,184,168,0.06)", border: `1px solid ${GM.amethystDim}`,
              animation: "vilGrant 0.8s ease-out 0.3s backwards",
            }}>
              <div style={{ fontSize: 11, letterSpacing: 3, color: GM.amethystBright, marginBottom: 7 }}>
                THE ROAD PAYS OUT
              </div>
              <div style={{ fontFamily: GM.fontBody, fontSize: 16, color: GM.vellum, lineHeight: 1.55 }}>
                {V.arriveGrant}
              </div>
              {runSummary && (
                <div style={{ marginTop: 12, display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, color: GM.vellumDim }}>
                  <span>PARRIES {runSummary.parries}</span>
                  <span>PERFECTS {runSummary.perfects}</span>
                  <span>RUINS {runSummary.ruins}</span>
                  <span>BEST CHAIN {runSummary.bestChain}</span>
                </div>
              )}
            </div>
            <button onClick={() => setTab("hub")} style={{
              width: "100%", marginTop: 20, padding: "13px 0", borderRadius: 10, cursor: "pointer",
              background: "linear-gradient(175deg, #a8601e, #4a2810)",
              border: `1px solid ${GM.inkGold}`, color: "#ffe6c2",
              fontFamily: GM.font, fontSize: 14, letterSpacing: 5,
            }}>ENTER THE VILLAGE</button>
          </div>
        ) : (
          <div style={{ width: "min(860px, 96vw)", display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
            {/* ── SPEAK ── */}
            <div style={{ ...panel, animation: "vilIn 0.5s ease-out" }}>
              <SectionTitle>THE VILLAGE SPEAKS</SectionTitle>
              <div style={{ minHeight: 118 }}>
                <div style={{ fontSize: 11, letterSpacing: 2, color: GM.inkGoldBright, marginBottom: 6 }}>
                  {speaker.who.toUpperCase()}
                </div>
                <div style={{ fontFamily: GM.fontBody, fontSize: 16, lineHeight: 1.6, color: GM.vellum }}>
                  “{speaker.text}”
                </div>
              </div>
              <button onClick={() => setSaid((s) => (s + 1) % V.speak.length)} style={ghost}>
                LISTEN AGAIN →
              </button>
            </div>

            {/* ── WHYLLIE'S COUNTER ──
                Extracted to Outfitter.js 2026-07-27 so the main
                menu can reach it too. This screen renders the same
                component the Constellation does; there is exactly
                one shop in this game and it lives in one file. */}
            <div style={{ ...panel, animation: "vilIn 0.5s ease-out 0.06s backwards" }}>
              <Outfitter soul={soul} crests={crests} kindling={kindling} onBuy={onBuy} showHeader={false} />
            </div>

            {/* ── THE ROAD ON ── */}
            <div style={{ ...panel, animation: "vilIn 0.5s ease-out 0.12s backwards" }}>
              <SectionTitle>THE ROAD ON</SectionTitle>
              {points > 0 && (
                <div style={{
                  marginBottom: 12, padding: "9px 12px", borderRadius: 9,
                  background: "rgba(43,184,168,0.07)", border: `1px solid ${GM.amethystDim}`,
                  fontSize: 12, color: GM.amethystBright,
                }}>
                  {/* ⋔, not Renown. Renown was retired Empire-wide on
                      2026-07-29 and this line had not heard. */}
                  {points} Ascension{points > 1 ? "s" : ""} ready to spend.
                  {" "}{GLYPH.kd} {RULES.ASCEND_COST} buys the next one.
                </div>
              )}

              {/* ── EAST — the road you have not walked yet ──
                  FIRST, and styled as the primary, because it is the
                  thing a player who just arrived actually wants. The
                  old `VILLAGE.departLocked` string it replaces has
                  been `undefined` since the Drowned Mile pass and
                  has been rendering nothing at all. */}
              {canDepart ? (
                <>
                  <button onClick={onDepart} style={primary}>{V.departLabel}</button>
                  <div style={{ fontSize: 11, color: GM.vellumFaint, lineHeight: 1.5, margin: "2px 0 12px" }}>
                    {V.departLine}
                  </div>
                </>
              ) : (
                <div style={{
                  fontFamily: GM.fontBody, fontSize: 14, lineHeight: 1.6,
                  color: GM.vellumFaint, marginBottom: 14,
                }}>{V.departLine}</div>
              )}

              <button onClick={onReplay} style={primary}>{V.replayLabel}</button>
              <div style={{ fontSize: 11, color: GM.vellumFaint, lineHeight: 1.5, margin: "2px 0 10px" }}>
                {V.replayLine}
              </div>
              <button onClick={onCustomize} style={primary}>THE EMBER CONSTELLATION</button>
              <button onClick={onCodex} style={ghost}>CODEX</button>
              <button onClick={onMenu} style={ghost}>MAIN MENU</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const primary = {
  width: "100%", padding: "12px 0", marginBottom: 8, borderRadius: 10, cursor: "pointer",
  background: "linear-gradient(175deg, #a8601e, #4a2810)",
  border: `1px solid ${GM.inkGold}`, color: "#ffe6c2",
  fontFamily: GM.font, fontSize: 13, letterSpacing: 4,
};
const ghost = {
  width: "100%", padding: "11px 0", marginTop: 8, borderRadius: 10, cursor: "pointer",
  background: "linear-gradient(175deg, #1c1009, #0d0705)",
  border: `1px solid ${GM.pageBorder}`, color: GM.vellumDim,
  fontFamily: GM.font, fontSize: 12, letterSpacing: 3,
};

function SectionTitle({ children }) {
  return (
    <div style={{
      fontFamily: GM.font, fontSize: 12, letterSpacing: 4, color: GM.inkGoldBright,
      marginBottom: 12, textShadow: `0 0 12px ${GM.inkGoldDim}`,
    }}>{children}</div>
  );
}
